import { useState } from 'react';
import { KeyRound, MessageSquareText, ShieldCheck, Wallet } from 'lucide-react';
import { postWalletLinkConfirm, postWalletLinkStart, walletError, type WalletInfo } from '@/api/parent';

const digits = (v: string, n: number) => v.replace(/\D/g, '').slice(0, n);

/** "You already have an OJP wallet": continue with it here after a code (SMS, email, in-app) and your transaction PIN. */
export function LinkExisting({ w, onDone, onNew }: { w: WalletInfo; onDone: (msg: string) => void; onNew: () => void }) {
  const e = w.existing_wallet!;
  const [step, setStep] = useState<'offer' | 'code'>('offer');
  const [sentTo, setSentTo] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const send = async () => {
    setErr(''); setBusy(true);
    try { const r = await postWalletLinkStart(); setSentTo(r.data.sent_to); setStep('code'); setCode(''); }
    catch (x) { setErr(walletError(x).message); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setErr(''); setBusy(true);
    try { const r = await postWalletLinkConfirm(code, pin); onDone(r.message); }
    catch (x) { setErr(walletError(x).message); setPin(''); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 space-y-4">
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-2xl bg-[#0A2540] text-white grid place-items-center shrink-0"><Wallet className="w-5 h-5" /></span>
        <div>
          <h2 className="text-lg font-extrabold text-[#0A2540]">You already have an OJP wallet</h2>
          <p className="text-sm text-slate-600 mt-0.5">You opened it at <b>{e.opened_at_school ?? 'another school'}</b>. Use the same wallet here: one balance and one account number for all your children’s schools. No BVN needed again.</p>
        </div>
      </div>
      <div className="rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <div><p className="text-[11px] uppercase tracking-wide font-bold text-slate-400">Your wallet</p><p className="font-mono font-bold text-lg text-[#0A2540]">{e.account_number}</p></div>
        <p className="text-sm text-slate-600">{e.account_name}{e.bank_name ? ` · ${e.bank_name}` : ''}</p>
      </div>

      {step === 'offer' ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={send} disabled={busy} className="flex-1 py-3 rounded-xl bg-[#0A2540] text-white text-sm font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4" />{busy ? 'Sending code…' : 'Continue with this wallet'}</button>
          <button onClick={onNew} className="sm:w-auto px-4 py-3 rounded-xl border border-slate-300 text-sm font-semibold text-slate-700">This is not my wallet</button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600 flex gap-2"><MessageSquareText className="w-4 h-4 shrink-0 mt-0.5 text-[#0A2540]" />We sent a 6-digit code by {sentTo.join(', ')}. It expires in 10 minutes.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-sm font-semibold text-[#0A2540]">Code</span>
              <input inputMode="numeric" autoFocus value={code} onChange={(x) => setCode(digits(x.target.value, 6))} placeholder="••••••" className="w-full mt-1 border-2 border-slate-300 rounded-xl px-3 py-2.5 text-center text-xl tracking-[0.4em] font-mono focus:border-[#0A2540] outline-none" /></label>
            <label className="block"><span className="text-sm font-semibold text-[#0A2540] inline-flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" />Transaction PIN</span>
              <input type="password" inputMode="numeric" value={pin} onChange={(x) => setPin(digits(x.target.value, 4))} placeholder="••••" className="w-full mt-1 border-2 border-slate-300 rounded-xl px-3 py-2.5 text-center text-xl tracking-[0.5em] font-mono focus:border-[#0A2540] outline-none" /></label>
          </div>
          <p className="text-[11px] text-slate-500">The PIN you use with this wallet at {e.opened_at_school ?? 'the other school'}. After this, the same PIN works here.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={confirm} disabled={busy || code.length !== 6 || pin.length !== 4} className="flex-1 py-3 rounded-xl bg-[#0A2540] text-white text-sm font-bold disabled:opacity-60">{busy ? 'Checking…' : 'Confirm and use this wallet'}</button>
            <button onClick={send} disabled={busy} className="px-4 py-3 rounded-xl border border-slate-300 text-sm font-semibold text-slate-700">Send the code again</button>
          </div>
        </div>
      )}
      {err && <p className="text-sm text-red-600" role="alert">{err}</p>}
    </section>
  );
}
